/* eslint-disable */
import React from 'react';
import { sessionInfo } from 'redux/actions';
import { useDispatch } from 'react-redux';
import { windowStore } from 'util/windowStore';
import { cacheKey, authTypes, SSO_PATH_PREFIX } from 'appConstants';
import { useLocation } from 'react-router-dom';
import { path } from 'routes/path';

export const useAuthenticateUser = (user, auth0ClientInitiated) => {
  const dispatch = useDispatch();
  const location = useLocation();

  /**
   * Check cache for JWT (used for legacy auth) and Auth0 Provider.
   */
  const token = windowStore.getToken();
  const cachedProviders = windowStore.get(cacheKey.PROVIDERS);
  const cachedAuth0Provider = (cachedProviders || []).find(
    (auth) => auth.authType === authTypes.AUTH0,
  );

  /**
   * Support for Angular-app as user may switch back and forth.
   * - Look for cached key starting with `@@auth0`
   * - if found, use its value to initialize auth0 client and authenticate user
   * This can be removed when the Angular (legacy) app is deprecated
   */
  const cachedAuth0ProviderAngualr = Object.keys(localStorage).find((item) =>
    item.includes(cacheKey.AUTH0_PREFIX),
  );

  /**
   * Change state to "is refreshing token" if any cached auth method is discovered.
   */
  const hasToken = Boolean(token || cachedAuth0Provider || cachedAuth0ProviderAngualr);
  const [isRefreshingToken, setIsRefreshingToken] = React.useState(hasToken);

  React.useEffect(() => {
    const authenticateUser = async () => {
      if (!user.authenticated && (token || auth0ClientInitiated)) {
        await dispatch(sessionInfo());
        setIsRefreshingToken(false);
      } else if (!user.authenticated && !token && !auth0ClientInitiated) {
        /**
         * @@auth0spajs@@ may exist in localStorage but only contain a refresh_token
         * with no decodedToken, meaning useAuth0 could not initialise the Auth0 client.
         * In that case only clear the stale entry so the user is redirected to login.
         */
        const staleKeys = Object.keys(localStorage).filter((key) => {
          if (!key.includes(cacheKey.AUTH0_PREFIX)) return false;
          try {
            const parsed = JSON.parse(localStorage.getItem(key));
            return !parsed?.body?.decodedToken;
          } catch {
            return true;
          }
        });

        if (staleKeys.length > 0) {
          staleKeys.forEach((key) => localStorage.removeItem(key));
          setIsRefreshingToken(false);
        }
      }
    };
    /**
     * Skip on /iam and /sso/ — those pages log the user in themselves.
     * On /sso/, calling sessionInfo() here breaks the SSO token before
     * the Sso component can use it.
     */
    if (location.pathname === path.IAM || location.pathname.startsWith(SSO_PATH_PREFIX)) {
      setIsRefreshingToken(false);
    } else {
      authenticateUser();
    }
  }, [dispatch, auth0ClientInitiated, user.authenticated, token, location.pathname]);

  return { isRefreshingToken };
};
